import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiClient } from './client';

const FORM_URLENCODED = 'application/x-www-form-urlencoded';

/**
 * Serialize request body for `application/x-www-form-urlencoded`.
 * `FormData` / `ArrayBuffer` / typed arrays are passed through unchanged (caller sets headers).
 */
export function serializeUrlEncodedBody(data: unknown): string | undefined {
  if (data === undefined || data === null) return undefined;
  if (typeof data === 'string') return data;
  if (data instanceof URLSearchParams) return data.toString();
  if (data instanceof FormData) return undefined;
  if (ArrayBuffer.isView(data) || data instanceof ArrayBuffer) return undefined;
  if (typeof data === 'object' && !Array.isArray(data)) {
    const p = new URLSearchParams();
    for (const [key, val] of Object.entries(data as Record<string, unknown>)) {
      if (val === undefined || val === null) continue;
      p.append(key, String(val));
    }
    return p.toString();
  }
  return String(data);
}

function mergeFormPostConfig(
  data: unknown,
  config?: AxiosRequestConfig
): { payload: unknown; axiosConfig: AxiosRequestConfig | undefined } {
  if (data instanceof FormData || ArrayBuffer.isView(data) || data instanceof ArrayBuffer) {
    return { payload: data, axiosConfig: config };
  }

  const encoded = serializeUrlEncodedBody(data);
  if (encoded === undefined) {
    return { payload: undefined, axiosConfig: config };
  }

  return {
    payload: encoded,
    axiosConfig: {
      ...config,
      headers: {
        ...(config?.headers as Record<string, string>),
        'Content-Type': FORM_URLENCODED,
      },
    },
  };
}

/**
 * Returns full AxiosResponse (status, headers, data). Use when you need metadata.
 */
export function request<T = unknown>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
  const method = (config.method || 'get').toLowerCase();
  if (method === 'post' || method === 'put' || method === 'patch') {
    const { payload, axiosConfig } = mergeFormPostConfig(config.data, config);
    return apiClient.request<T>({ ...config, ...axiosConfig, data: payload });
  }
  return apiClient.request<T>(config);
}

/** Unwraps `response.data` for typical JSON APIs. */
export const http = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((r) => r.data),

  post: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) => {
    const { payload, axiosConfig } = mergeFormPostConfig(data, config);
    return apiClient.post<T>(url, payload, axiosConfig).then((r) => r.data);
  },

  put: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) => {
    const { payload, axiosConfig } = mergeFormPostConfig(data, config);
    return apiClient.put<T>(url, payload, axiosConfig).then((r) => r.data);
  },

  patch: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) => {
    const { payload, axiosConfig } = mergeFormPostConfig(data, config);
    return apiClient.patch<T>(url, payload, axiosConfig).then((r) => r.data);
  },

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((r) => r.data),

  /** Raw instance for custom calls, upload progress, cancel tokens, etc. */
  client: apiClient,
};
